/**
 * DRIFT — the Channel One hero force. Pure math, no Web Audio.
 *
 * Per the Broadcast #1 Realm Specification section 5: 0 = local (the signal nearly
 * direct), 1 = deep space (arrived across vast distance, still musical and
 * intelligible). Perceptual curve: gentle through the first third, opening
 * progressively. Every position musical; no dead zones, no cliff.
 *
 * Kept pure so it is testable without a browser.
 */
export interface DriftParams {
  /** Wet mix, equal-power. 0..1 */
  wet: number;
  /** Dry mix, equal-power complement. 0..1 */
  dry: number;
  /** Reverb pre-delay, seconds. */
  preDelay: number;
  /** Reverb tail, seconds. */
  size: number;
  /** Detune spread, cents (applied +/- across voices). */
  detuneCents: number;
  /** High shelf corner, Hz — distance filters, never muffles into mud. */
  highShelfHz: number;
  /** High shelf gain, dB (negative). */
  highShelfDb: number;
  /** Low cut, Hz. */
  lowCutHz: number;
  /** Interstellar floor level, linear. Signal-gated elsewhere. */
  floorLevel: number;
}

/** Caps the wet/dry crossfade so dry never reaches zero. See driftParams. */
const DRY_FLOOR_ANGLE = 0.86;

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

/**
 * Perceptual curve. Gentle early (nearness has fine gradations), opening later.
 * Monotonic, continuous, f(0)=0, f(1)=1.
 */
export const driftCurve = (x: number): number => {
  const t = clamp01(x);
  return t * t * (3 - 2 * t) * 0.65 + t * 0.35;
};

export function driftParams(knob: number): DriftParams {
  const d = driftCurve(knob);
  // The crossfade angle is capped below 90 degrees so the dry signal never fully
  // vanishes. Realm Spec section 5 requires deep space to remain "musical and
  // intelligible"; a full equal-power crossfade to wet at 1.0 removes the source
  // entirely, which fails the section 8.1 acceptance criterion and flattens the top
  // of the knob into a dead zone.
  const angle = d * DRY_FLOOR_ANGLE * (Math.PI / 2);
  const wet = Math.sin(angle);
  const dry = Math.cos(angle);
  return {
    wet,
    dry,
    preDelay: lerp(0.004, 0.075, d),
    size: lerp(0.6, 5.2, d),
    detuneCents: lerp(2, 9, d),
    highShelfHz: lerp(16000, 5200, d),
    highShelfDb: lerp(0, -9, d),
    lowCutHz: lerp(30, 170, d),
    floorLevel: lerp(0, 0.02, d),
  };
}

/**
 * Acceptance criterion, Realm Spec section 8.1: speech must remain intelligible at
 * every DRIFT position. Intelligibility depends chiefly on the 1-4 kHz band surviving,
 * so the high shelf must never encroach on it and dry must never fully vanish.
 */
export const speechIntelligible = (p: DriftParams): boolean =>
  p.highShelfHz >= 4000 && p.dry > 0.02 && p.highShelfDb > -12;
